import { UserAssessment } from '@api/assessmentQuestionnaireApiHandler';
import { MotionText } from '@shared/atoms';
import { NoDataBlueTraySection, SystemButton } from '@shared/molecules';
import { CheckIn } from '@shared/organisms/Assessments/CheckIn/CheckIn';
import { ApexOptions } from 'apexcharts';
import React, { useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import styles from './index.module.scss';

interface WellbeingTrendsProps {
  wellbeingTrendsData: UserAssessment[];
  refetchCheckInAssessments: () => void;
}

export const WellbeingTrends: React.FC<WellbeingTrendsProps> = ({
  wellbeingTrendsData,
  refetchCheckInAssessments,
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  const processChartData = () => {
    if (!wellbeingTrendsData || wellbeingTrendsData.length === 0) {
      return {
        series: [{ name: 'Wellbeing Score', data: [] }],
        categories: [],
        scores: [],
      };
    }

    const sortedData = [...wellbeingTrendsData].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );

    const scores = sortedData.map((item) => item.totalScore);
    const categories = sortedData.map((item) => {
      const date = new Date(item.createdAt);

      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    });

    return {
      series: [{ name: 'Wellbeing Score', data: scores }],
      categories,
      scores,
    };
  };

  const { series, categories, scores } = processChartData();

  const getYAxisRange = () => {
    if (scores.length === 0) return { min: 0, max: 100 };

    const minScore = Math.min(...scores);
    const maxScore = Math.max(...scores);
    const padding = Math.max(10, (maxScore - minScore) * 0.1);

    return {
      min: Math.max(0, Math.floor(minScore - padding)),
      max: Math.min(100, Math.ceil(maxScore + padding)),
    };
  };

  const yAxisRange = getYAxisRange();

  const chartOptions: ApexOptions = {
    chart: {
      type: 'line',
      height: 220,
      toolbar: {
        show: false,
      },
      animations: {
        enabled: true,
      },
      fontFamily: 'inherit',
    },
    colors: ['#3498db'],
    stroke: {
      curve: 'smooth',
      width: 3,
    },
    grid: {
      borderColor: '#f1f1f1',
      row: {
        colors: ['transparent'],
      },
    },
    markers: {
      size: 4,
      colors: ['#fff'],
      strokeColors: '#3498db',
      strokeWidth: 2,
    },
    xaxis: {
      categories,
      labels: {
        style: {
          fontSize: '10px',
          fontFamily: 'inherit',
        },
      },
      axisBorder: {
        show: false,
      },
    },
    yaxis: {
      min: 0,
      max: 100,
      tickAmount: 5,
      labels: {
        formatter: (value: number) => value.toFixed(0),
        style: {
          fontSize: '10px',
          fontFamily: 'inherit',
        },
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (value: number) => `${value} points`,
      },
    },
    dataLabels: {
      enabled: false,
    },
    legend: {
      show: false,
    },
  };

  const generateChartDescription = () => {
    if (scores.length === 0) {
      return 'No wellbeing data available to display.';
    }

    const descriptions = scores.map((score, index) => `${score} points on ${categories[index]}`);

    return `Your wellbeing scores over time: ${descriptions.join(', ')}.`;
  };

  return (
    <div className={styles.trendsSection} aria-labelledby="trends-heading">
      <div className={styles.trendsHeader}>
        <MotionText
          className={styles.sectionTitle}
          id="trends-heading"
          as="h3"
          text="Your check-in trends"
        />

        <SystemButton
          className={styles.checkInButton}
          aria-label="Check in"
          onClick={() => setModalOpen(true)}
        >
          Check In
        </SystemButton>
      </div>

      <MotionText
        className={styles.trackProgressText}
        id="trends-heading"
        as="text"
        text="Track your progress"
      />
      <div
        className={styles.trendsChart}
        aria-label="Wellbeing trends chart showing scores over time"
      >
        {scores.length > 0 ? (
          <ReactApexChart
            options={chartOptions}
            series={series}
            type="line"
            height={220}
            aria-hidden="true"
          />
        ) : (
          <div className={styles.emptyChart}>
            <NoDataBlueTraySection text="No data available. Start tracking your wellbeing!" />
          </div>
        )}

        <div className="visually-hidden">{generateChartDescription()}</div>
      </div>

      {modalOpen && (
        <CheckIn
          handleStep={() => null}
          modalOpen={modalOpen}
          handleModalOpen={setModalOpen}
          refetchCheckInAssessments={refetchCheckInAssessments}
        />
      )}
    </div>
  );
};
